// src/components/FishIQCalibrationPanel.tsx
import React, { useCallback, useEffect, useState } from 'react';
import { cfAdminGetFishIQCalibration } from '../services/adminClient';

const Metric = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="rounded-2xl border border-[#D8C4AA] bg-[#FFFDF8] p-3 dark:border-[rgba(132,151,190,0.18)] dark:bg-[#101827]">
    <div className="text-[10px] font-black uppercase tracking-[0.16em] text-[#7B5E4C] dark:text-[#AEB8C9]">{label}</div>
    <div className="mt-1 text-2xl font-black text-[#22313A] dark:text-white">{value}</div>
  </div>
);

export default function FishIQCalibrationPanel() {
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try { const res = await cfAdminGetFishIQCalibration({ limit: 1500 }); setData(res.data); }
    catch (err: any) { setError(String(err?.message || err)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { void load(); }, [load]);

  return (
    <section className="rounded-2xl border border-[#D8C4AA] bg-[#FFFDF8] p-4 shadow-sm dark:border-[rgba(132,151,190,0.18)] dark:bg-[#101827]">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs font-black uppercase tracking-[0.18em] text-[#7B5E4C] dark:text-[#AEB8C9]">FishIQ calibration</div>
          <h3 className="mt-1 text-lg font-black text-[#22313A] dark:text-white">Evidence quality + confidence honesty</h3>
          <p className="mt-1 text-xs font-semibold text-[#5B6577] dark:text-[#AEB8C9]">Aggregate-only checks. No private catches or exact spots.</p>
        </div>
        <button type="button" onClick={() => void load()} disabled={loading} className="rounded-xl bg-[#24313A] px-3 py-2 text-xs font-black text-white disabled:opacity-50 dark:bg-[#E8EEF7] dark:text-[#0A1120]">{loading ? 'Loading…' : 'Refresh'}</button>
      </div>
      {error ? <div className="mt-3 rounded-xl border border-red-200 bg-red-50 p-3 text-xs font-bold text-red-700">{error}</div> : null}
      <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Metric label="Signals scanned" value={data?.scanned ?? '—'} />
        <Metric label="Surfaceable" value={data?.surfaceable ?? '—'} />
        <Metric label="Hidden" value={data?.hidden ?? '—'} />
        <Metric label="Avg quality" value={data ? `${data.averageObservationQuality}%` : '—'} />
        <Metric label="Strong risk" value={data?.strongWithoutDiversity ?? '—'} />
      </div>
      {data ? <div className="mt-3 grid gap-3 md:grid-cols-3">
        {[['Confidence tiers', data.tierCounts], ['Quality bands', data.qualityBands], ['Freshness', data.freshnessBands]].map(([title, rows]: any) => (
          <div key={title} className="rounded-2xl border border-[#D8C4AA] bg-[#FFF8ED] p-3 dark:border-[rgba(132,151,190,0.18)] dark:bg-[#151F31]">
            <div className="text-[11px] font-black uppercase tracking-[0.16em] text-[#6F5A44] dark:text-[#AEB8C9]">{title}</div>
            <div className="mt-2 space-y-1">{Object.entries(rows || {}).map(([k,v]) => <div key={k} className="flex justify-between text-xs font-bold"><span>{k}</span><span>{String(v)}</span></div>)}</div>
          </div>
        ))}
      </div> : null}
    </section>
  );
}
